import { useMutation } from '@tanstack/react-query';
import { useApi } from '../../lib/api';

// Hand-written: the generated schema types `secrets` as Record<string, never>.
export interface RevealedSecret {
  operation_id: string;
  secrets: Record<string, unknown>;
}

export interface RevealSecretError extends Error {
  status?: number;
  body?: { error?: string };
}

/**
 * Turns an openapi-fetch result into the revealed secret or a thrown error.
 * Success is decided by the HTTP status, not by `error`: openapi-fetch gives
 * `error === ""` for a failed response with an empty body (a gateway 502),
 * which is falsy and would otherwise pass as success with no data.
 */
export function revealResult(result: {
  data?: unknown;
  error?: unknown;
  response?: Response;
}): RevealedSecret {
  const { data, error, response } = result;
  if (!response?.ok || !data) {
    const e = new Error('mcp_reveal_failed') as RevealSecretError;
    if (response) e.status = response.status;
    if (typeof error === 'object' && error !== null) e.body = error as { error?: string };
    throw e;
  }
  return data as RevealedSecret;
}

export function useRevealSecret() {
  const api = useApi();
  return useMutation({
    mutationFn: async ({ id }: { id: string }): Promise<RevealedSecret> =>
      revealResult(
        await api.POST('/auth/mcp/secret-reveals/{id}', {
          params: { path: { id } },
        }),
      ),
  });
}
